// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — turning the state into the handful of things a player actually has to see.
//
// Pure. No DOM, no measurement, no colours. Everything here is testable arithmetic, which matters
// more in this game than in most: the hardest part of Sparklight on a phone is not drawing it, it is
// deciding WHAT NOT to draw. A player holding four unknown cards, watching four other hands, needs
// to answer three questions at a glance — what can I raise, what must nobody throw away, and what is
// already lost — and a faithful rendering of the full state answers none of them.
//
// ── THE CENSUS REPLACES THE DISCARD PILE ─────────────────────────────────────────────────────────
//
// A physical game of this shape needs an ash heap you can rummage through, and every digital version
// reproduces it: a growing row of small cards that nobody reads. It exists to answer exactly one
// question — "how many copies of that are left?" — so this game answers that question directly
// instead, with a five-cell ladder beside every plume. Thirty marks, permanently on screen, and the
// ash heap is not rendered at all.

import { TOP, copiesOf, rankOf, suitOf } from './cards';
import { candidatesFor, chopOf, copiesRemaining, intentsFrom, isCritical, type Intent } from './bot';
import { HIDDEN, cardAt, isTouched, raisable, type Game } from './game';

/** What has become of one rank of one suit. Shape carries it; colour is redundant reinforcement. */
export type CensusCell =
  /** Already up on the plume. */
  | 'raised'
  /** More than one copy still out there — safe to lose. */
  | 'spare'
  /** Exactly one left anywhere. Douse it and the plume stops here for ever. */
  | 'last'
  /** Every copy has burned. The plume can never pass the rank below this one. */
  | 'lost';

export interface PlumeView {
  suit: number;
  name: string;
  glyph: string;
  height: number;
  /** Index 0 is rank 1. */
  ladder: CensusCell[];
  /** The plume can never be finished, because some rank above it is gone. */
  capped: boolean;
}

/** The census: for every suit, what has happened to each of its five ranks. */
export function plumeViews(g: Game): PlumeView[] {
  return g.suits.map((s) => {
    const ladder: CensusCell[] = [];
    let capped = false;
    for (let r = 1; r <= TOP; r++) {
      if (g.plumes[s.id] >= r) {
        ladder.push('raised');
        continue;
      }
      const left = copiesRemaining(g, s.id * 8 + r);
      if (left === 0) {
        ladder.push('lost');
        capped = true;
      } else if (left === 1) ladder.push('last');
      else ladder.push('spare');
    }
    return { suit: s.id, name: s.name, glyph: s.glyph, height: g.plumes[s.id], ladder, capped };
  });
}

/** Where a slot stands, once everything its holder has been told is taken into account. */
export type Verdict =
  /** Every card it could be is raisable right now. Play it. */
  | 'go'
  /** Every card it could be has already been raised. Free to douse. */
  | 'spent'
  /** Every card it could be is the last of its kind. Never douse this. */
  | 'last'
  /** Not narrowed down enough to say. */
  | 'open';

export interface SlotView {
  slot: number;
  /** Suit ids this card could still be. */
  suits: number[];
  /** Ranks it could still be. */
  ranks: number[];
  /** Non-null when it has been narrowed to exactly one card. */
  known: number | null;
  /** Directly pointed at, as opposed to worked out. The player should be able to tell them apart. */
  toldSuit: boolean;
  toldRank: boolean;
  verdict: Verdict;
  /** What the crew meant by the hint that touched it, if anything. */
  intent: Intent | null;
  /** The card that would be doused next — the one a save hint is racing. */
  isChop: boolean;
}

/**
 * Your own hand, as you are entitled to understand it.
 *
 * `counts` is on: striking off every copy you can see in somebody else's hand, in the ash or under a
 * raised plume is not an advanced technique, it is how the game is played, and hiding it from the
 * player while the bot does it would be a strange kind of handicap.
 */
export function handView(g: Game, seat: number): SlotView[] {
  const intents = intentsFrom(g);
  const chop = chopOf(g, seat);
  return g.hands[seat].map((pos, slot) => {
    const cs = candidatesFor(g, seat, slot, true);
    const suits = [...new Set([...cs.weights.keys()].map(suitOf))].sort((a, b) => a - b);
    const ranks = [...new Set([...cs.weights.keys()].map(rankOf))].sort((a, b) => a - b);
    const cards = [...cs.weights.keys()];
    const k = g.known[seat][slot];
    let verdict: Verdict = 'open';
    if (cards.length > 0) {
      if (cards.every((c) => raisable(g, c))) verdict = 'go';
      else if (cards.every((c) => g.plumes[suitOf(c)] >= rankOf(c))) verdict = 'spent';
      else if (cards.every((c) => isCritical(g, c))) verdict = 'last';
    }
    return {
      slot,
      suits,
      ranks,
      known: cards.length === 1 ? cards[0] : null,
      toldSuit: k.yes !== 0,
      toldRank: k.rankYes !== 0,
      verdict,
      intent: intents.get(pos) ?? null,
      isChop: slot === chop,
    };
  });
}

export interface MateCard {
  slot: number;
  card: number;
  toldSuit: boolean;
  toldRank: boolean;
  /** Their teammate knows this one is playable and has been told so. */
  intent: Intent | null;
  /** It is raisable right now — the thing you might want to point at. */
  raisable: boolean;
  /** Irreplaceable. If it is also on their chop, it is about to be lost. */
  critical: boolean;
  isChop: boolean;
}

export interface SeatView {
  seat: number;
  name: string;
  you: boolean;
  active: boolean;
  /** Nobody is behind this seat any more; the crew is covering it. */
  away: boolean;
  bot: boolean;
  cards: MateCard[];
}

export function seatViews(g: Game, you: number | null, names: string[], away: Set<number>, bots: Set<number>): SeatView[] {
  const intents = intentsFrom(g);
  const out: SeatView[] = [];
  for (let seat = 0; seat < g.setup.players; seat++) {
    const chop = chopOf(g, seat);
    out.push({
      seat,
      name: names[seat] ?? `Seat ${seat + 1}`,
      you: seat === you,
      active: g.turn === seat && !g.over,
      away: away.has(seat),
      bot: bots.has(seat),
      cards: g.hands[seat].map((pos, slot) => {
        const c = cardAt(g, pos);
        const k = g.known[seat][slot];
        return {
          slot,
          card: c,
          toldSuit: k.yes !== 0,
          toldRank: k.rankYes !== 0,
          intent: intents.get(pos) ?? null,
          raisable: c !== HIDDEN && raisable(g, c),
          critical: c !== HIDDEN && isCritical(g, c),
          isChop: slot === chop,
        };
      }),
    });
  }
  return out;
}

/** Which of a target's slots a hint would light. For the preview the giver sees before spending. */
export function previewSlots(g: Game, target: number, kind: number, value: number): number[] {
  const out: number[] = [];
  const hand = g.hands[target];
  for (let i = 0; i < hand.length; i++) {
    const c = cardAt(g, hand[i]);
    if (c === HIDDEN) continue;
    const hit =
      kind === 0
        ? suitOf(c) === value || (g.suits.find((s) => s.id === suitOf(c))?.wild ?? false)
        : rankOf(c) === value;
    if (hit) out.push(i);
  }
  return out;
}

// ── the summary ───────────────────────────────────────────────────────────────────────────────────

export interface Contribution {
  seat: number;
  name: string;
  raised: number;
  failed: number;
  pointed: number;
  doused: number;
  /** Cards this seat doused that could never be replaced. Shown as a fact, never as a ranking. */
  lostForever: number;
}

export interface Summary {
  score: number;
  perfect: number;
  reason: string;
  /** The one line that says what happened to the crew, not to you. */
  headline: string;
  plumes: PlumeView[];
  /** The plumes that could still have grown, and what stopped them. */
  missed: Array<{ suit: number; name: string; glyph: string; height: number; blockedAt: number | null }>;
  crew: Contribution[];
}

/**
 * The end of a night.
 *
 * Co-op inverts the usual rule: the SHARED outcome leads, and the per-player breakdown is there to
 * show what each person contributed, never to rank them. So `crew` is rendered in seat order and
 * never sorted by score — a co-op summary that quietly becomes a leaderboard teaches people to hog
 * the thing that scores, which in this game is raising, which is the one job that is supposed to be
 * shared out.
 */
export function summarise(g: Game, names: string[]): Summary {
  const players = g.setup.players;
  const crew: Contribution[] = Array.from({ length: players }, (_, seat) => ({
    seat,
    name: names[seat] ?? `Seat ${seat + 1}`,
    raised: 0,
    failed: 0,
    pointed: 0,
    doused: 0,
    lostForever: 0,
  }));

  // Re-derive what each douse cost by walking the ash as it grew, so "the last copy" means the last
  // copy AT THAT MOMENT rather than at the end.
  const burned = new Map<number, number>();
  for (const e of g.events) {
    if (e.k === 'raise') {
      if (e.ok) crew[e.seat].raised++;
      else {
        crew[e.seat].failed++;
        burned.set(e.card, (burned.get(e.card) ?? 0) + 1);
      }
    } else if (e.k === 'point') crew[e.seat].pointed++;
    else if (e.k === 'douse') {
      crew[e.seat].doused++;
      burned.set(e.card, (burned.get(e.card) ?? 0) + 1);
      const spec = g.suits.find((s) => s.id === suitOf(e.card));
      // Only a loss if the plume never got there. Dousing the second Amber 2 after Amber has
      // already passed 2 costs the crew nothing, and calling it a loss on the summary would be
      // blaming somebody for the correct play.
      const gone = (burned.get(e.card) ?? 0) >= (spec ? copiesOf(spec, rankOf(e.card)) : 99);
      if (spec && gone && g.plumes[suitOf(e.card)] < rankOf(e.card)) crew[e.seat].lostForever++;
    }
  }

  const plumes = plumeViews(g);
  const perfect = g.suits.length * TOP;
  const scoreNow = g.plumes.reduce((a, b) => a + b, 0);
  const missed = plumes
    .filter((p) => p.height < TOP)
    .map((p) => ({
      suit: p.suit,
      name: p.name,
      glyph: p.glyph,
      height: p.height,
      blockedAt: p.ladder.findIndex((c) => c === 'lost') + 1 || null,
    }));

  const headline =
    g.reason === 'alone'
      ? 'Everyone else left. The night ended where it stood.'
      : g.reason === 'perfect'
      ? 'Every plume full. The sky is finished.'
      : g.reason === 'haze'
        ? 'The third haze closed the sky early.'
        : scoreNow >= perfect - 2
          ? 'The pile ran out with the sky almost complete.'
          : 'The pile ran out.';

  return { score: scoreNow, perfect, reason: g.reason ?? 'deck', headline, plumes, missed, crew };
}

export { isTouched };
